import { useEffect, useMemo, useRef, useState } from "react"
import { MarkdownPreview } from "@/components/features/markdown-preview"
import { PreviewPaneHeader } from "@/components/features/preview-pane-header"
import { PreviewFindBar } from "@/components/features/preview-find-bar"
import { QuizDocumentRenderer } from "@/components/integrations/quiz/QuizDocumentRenderer"
import { HtmlDocumentViewer } from "@/components/features/html-document-viewer"
import { isHtmlDocument, isCsvDocument, isImageDocument } from "@/components/features/document-list.utils"
import { ImageDocumentViewer } from "@/components/features/image-document-viewer"
import { CsvDocumentViewer } from "@/components/features/csv-document-viewer"
import { fetchWithAuth, getDocuments } from "@/lib/api"
import type { Document } from "@/lib/api"
import { ExtensionListRow } from "@/components/integrations/_shared/extension-list-row"
import { userFacingCopyPath } from "@/lib/app-routes"
import { parseMarkdownWithFrontmatter } from "@/lib/markdown-frontmatter"
import { testIds } from "@/lib/test-ids"
import { useWorkspace } from "@/providers/workspace-provider"
import { usePreviewFind } from "@/hooks/use-preview-find"
import {
  DocumentPeekMenuItems,
  PeekLocationLine,
  peekParentFolderLabel,
} from "@/components/features/document-peek-chrome"
import { FileText, Folder, FolderOpen, Mic, Loader2 } from "lucide-react"
import { getDocumentDisplayIcon } from "@/lib/document-icons"
import { toast } from "sonner"
import type { Document as ListDocument, ExplorerFolder, SelectedItem } from "./document-list.types"
import {
  folderContentTypeLine,
  folderCountLine,
  folderSelection,
  folderTagLine,
} from "./document-list.utils"
import "./tiptap-styles.css"

/** Returns true for documents that live under a bets/ directory. */
function isBetDocument(filePath?: string | null): boolean {
  return !!filePath && /\/bets\//.test(filePath)
}

function workspacesForRoutes(
  workspaces: { id: string; slug: string; contentRoot: string | null; name: string }[],
) {
  return workspaces.map((w) => ({
    id: w.id,
    slug: w.slug,
    contentRoot: w.contentRoot,
    name: w.name,
  }))
}

function openExplorerContextMenu(
  event: React.MouseEvent,
  context: Record<string, unknown>,
) {
  window.dispatchEvent(
    new CustomEvent("open-context-menu", {
      detail: { x: event.clientX, y: event.clientY, context },
    }),
  )
}

function FolderPeekList({
  folderPath,
  onSelectChild,
}: {
  folderPath: string
  onSelectChild?: (item: SelectedItem) => void
}) {
  const [folders, setFolders] = useState<ExplorerFolder[]>([])
  const [docs, setDocs] = useState<Document[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const [folderRes, docRes] = await Promise.all([
          fetchWithAuth(
            `/api/documents/explorer-subfolders?path=${encodeURIComponent(folderPath)}`,
          ),
          getDocuments({ folder: folderPath, limit: 40, offset: 0, sort: "recent" }),
        ])
        if (!folderRes.ok) throw new Error("Failed to load folders")
        const folderJson = await folderRes.json()
        const nextFolders = Array.isArray(folderJson.folders)
          ? folderJson.folders.map((f: ExplorerFolder) => ({
              name: f.name,
              path: f.path,
              fileCount: typeof f.fileCount === "number" ? f.fileCount : undefined,
              folderCount: typeof f.folderCount === "number" ? f.folderCount : undefined,
              hasSoul: f.hasSoul === true,
              contentTypes: Array.isArray(f.contentTypes) ? f.contentTypes : undefined,
              tags: Array.isArray(f.tags) ? f.tags : undefined,
            }))
          : []
        if (cancelled) return
        setFolders(nextFolders)
        setDocs(docRes.documents ?? [])
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load folder")
          setFolders([])
          setDocs([])
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [folderPath])

  if (loading) {
    return (
      <div className="flex items-center gap-2 px-4 py-4 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin shrink-0" />
        Loading folder...
      </div>
    )
  }
  if (error) {
    return <div className="px-4 py-4 text-sm text-destructive">{error}</div>
  }
  if (folders.length === 0 && docs.length === 0) {
    return (
      <p className="px-4 py-4 text-sm text-muted-foreground">This folder is empty.</p>
    )
  }

  return (
    <ul data-testid={testIds.documentList.previewFolderList}>
      {folders.map((folder) => (
        <li key={folder.path}>
          <ExtensionListRow
            onSelect={() => onSelectChild?.(folderSelection(folder))}
            onOpen={() => onSelectChild?.(folderSelection(folder))}
            onContextMenu={(event) =>
              openExplorerContextMenu(event, {
                type: "folder",
                value: folder.name,
                path: folder.path,
              })
            }
          >
            <Folder className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="min-w-0 flex-1 truncate text-sm font-medium">{folder.name}</div>
          </ExtensionListRow>
        </li>
      ))}
      {docs.map((doc) => (
        <li key={doc.id}>
          <ExtensionListRow
            onSelect={() => onSelectChild?.({ kind: "document", doc: doc as unknown as ListDocument })}
            onOpen={() => onSelectChild?.({ kind: "document", doc: doc as unknown as ListDocument })}
            onContextMenu={(event) => {
              const filePath = doc.filePath || doc.file_path || doc.id
              openExplorerContextMenu(event, {
                type: "document",
                value: doc.title,
                documentId: doc.id,
                filePath,
                path: filePath,
                tags: doc.tags,
              })
            }}
          >
            <FileText className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="min-w-0 flex-1 truncate text-sm font-medium">{doc.title}</div>
          </ExtensionListRow>
        </li>
      ))}
    </ul>
  )
}

export function DocumentPreviewPane({
  item,
  workspaceColor,
  onOpen,
  onClose,
  onSelectChild,
  initialFindQuery = "",
}: {
  item: SelectedItem
  workspaceColor?: string
  onOpen: (event?: React.MouseEvent) => void
  onClose: () => void
  /** Child of a peeked folder. Parent enters that folder in the main list. */
  onSelectChild?: (item: SelectedItem) => void
  /** When set (e.g. from Advanced Search), open find and jump to first match. */
  initialFindQuery?: string
}) {
  const { workspaces } = useWorkspace()
  const routeWorkspaces = useMemo(() => workspacesForRoutes(workspaces), [workspaces])
  const paneRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  const [previewMarkdown, setPreviewMarkdown] = useState("")
  const [previewFrontmatter, setPreviewFrontmatter] = useState<Record<string, unknown>>({})
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [docFilePath, setDocFilePath] = useState<string | null>(null)
  const [docMeta, setDocMeta] = useState<{
    title?: string
    type?: Document["type"]
    updatedAt?: string
  } | null>(null)

  const isFolder = item.kind === "folder"
  const documentId = item.kind === "document" ? item.doc.id : null
  const name = isFolder ? item.name : (docMeta?.title || item.doc.title)
  const docType = item.kind === "document" ? (docMeta?.type ?? item.doc.type) : null

  // filePath from list payload OR resolved after full fetch
  const listFilePath = item.kind === "document" ? (item.doc.filePath ?? null) : null
  const resolvedFilePath = docFilePath ?? listFilePath
  const isBet = isBetDocument(resolvedFilePath)
  const isHtml = !isFolder && isHtmlDocument({ type: docType, filePath: resolvedFilePath })
  const isCsv = !isFolder && isCsvDocument({ type: docType, filePath: resolvedFilePath })
  const isImage = !isFolder && isImageDocument({ type: docType, filePath: resolvedFilePath })

  const kindFromFm = String(previewFrontmatter.kind || "").toLowerCase()
  const showQuizUi =
    !isFolder &&
    !previewLoading &&
    !previewError &&
    (kindFromFm === "quiz" || docType === "quiz")

  const locationPath = isFolder ? item.path : resolvedFilePath
  const parentFolder = peekParentFolderLabel(locationPath, routeWorkspaces)

  const copyPath = resolvedFilePath
    ? userFacingCopyPath(resolvedFilePath, routeWorkspaces)
    : null

  const findEnabled =
    !isFolder &&
    !previewLoading &&
    !previewError &&
    !showQuizUi &&
    !isHtml &&
    !isCsv &&
    !isImage &&
    Boolean(previewMarkdown)

  const contentKey =
    findEnabled && documentId ? `${documentId}:${previewMarkdown.length}` : null

  const {
    open: findOpen,
    query: findQuery,
    current: findCurrent,
    total: findTotal,
    openFind,
    close: closeFind,
    setFindQuery,
    findNext,
    findPrev,
    focusRequestId: findFocusRequestId,
  } = usePreviewFind({
    containerRef: contentRef,
    contentKey,
    enabled: findEnabled,
    initialQuery: initialFindQuery,
  })

  useEffect(() => {
    if (!documentId) {
      setPreviewMarkdown("")
      setPreviewFrontmatter({})
      setPreviewLoading(false)
      setPreviewError(null)
      setDocFilePath(null)
      setDocMeta(null)
      return
    }

    const ac = new AbortController()
    setPreviewLoading(true)
    setPreviewError(null)
    setPreviewMarkdown("")
    setPreviewFrontmatter({})

    fetchWithAuth(`/api/documents/${encodeURIComponent(documentId)}`, { signal: ac.signal })
      .then(async (response) => {
        if (!response.ok) {
          const errText = await response.text().catch(() => "")
          throw new Error(errText || response.statusText || "Failed to load document")
        }
        return response.json() as Promise<{
          content?: string
          filePath?: string
          file_path?: string
          frontmatter?: Record<string, unknown>
          title?: string
          type?: Document["type"]
          updatedAt?: string
        }>
      })
      .then((data) => {
        const raw = typeof data.content === "string" ? data.content : ""
        const apiFm = (data.frontmatter ?? {}) as Record<string, unknown>
        const parsed = parseMarkdownWithFrontmatter(raw)
        const frontmatter =
          Object.keys(apiFm).length > 0
            ? { ...parsed.frontmatter, ...apiFm }
            : parsed.frontmatter

        setPreviewMarkdown(raw)
        setPreviewFrontmatter(frontmatter)
        setDocFilePath(data.filePath ?? data.file_path ?? null)
        setDocMeta({
          title: data.title,
          type: data.type,
          updatedAt: data.updatedAt,
        })
      })
      .catch((e: Error) => {
        if (e.name === "AbortError") return
        setPreviewError(e.message || "Could not load preview")
      })
      .finally(() => {
        if (!ac.signal.aborted) setPreviewLoading(false)
      })

    return () => ac.abort()
  }, [documentId])

  // Cmd/Ctrl+F when focus is inside this preview pane (capture so we beat browser find)
  useEffect(() => {
    if (!findEnabled) return

    const onKeyDown = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== "f") return
      const pane = paneRef.current
      const target = e.target as Node | null
      if (!pane || !target || !pane.contains(target)) return
      e.preventDefault()
      e.stopPropagation()
      openFind()
    }

    window.addEventListener("keydown", onKeyDown, true)
    return () => window.removeEventListener("keydown", onKeyDown, true)
  }, [findEnabled, openFind])

  const quizDocument: Document | null =
    showQuizUi && documentId
      ? {
          id: documentId,
          title: name,
          type: "quiz",
          excerpt: "",
          content: previewMarkdown,
          updatedAt: docMeta?.updatedAt || item.doc.updatedAt || "",
          updatedBy: item.doc.updatedBy || "",
          tags: item.doc.tags || [],
          filePath: resolvedFilePath ?? undefined,
          frontmatter: previewFrontmatter,
        }
      : null

  const imagePreviewDocument: Document | null =
    isImage && documentId
      ? {
          id: documentId,
          title: name,
          type: "image",
          excerpt: "",
          content: previewMarkdown,
          updatedAt: docMeta?.updatedAt || item.doc.updatedAt || "",
          updatedBy: item.doc.updatedBy || "",
          tags: item.doc.tags || [],
          filePath: resolvedFilePath ?? undefined,
          frontmatter: previewFrontmatter,
        }
      : null

  // ------------------------------------------------------------------
  // Header elements
  // ------------------------------------------------------------------

  const headerIcon = isFolder ? (
    <FolderOpen
      className="h-4 w-4 text-muted-foreground flex-shrink-0"
      style={{ color: workspaceColor ? workspaceColor + "cc" : undefined }}
    />
  ) : showQuizUi ? (
    getDocumentDisplayIcon(
      { kind: "quiz", type: "quiz" },
      { className: "h-4 w-4 flex-shrink-0" },
    )
  ) : docType === "voice_memo" ? (
    <Mic className="h-4 w-4 text-muted-foreground flex-shrink-0" />
  ) : (
    <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
  )

  const headerChips = (
    <PeekLocationLine
      parent={parentFolder}
      title={name}
      testId={
        isFolder
          ? testIds.documentList.previewFolderTitle
          : testIds.documentList.previewLocation
      }
    />
  )

  const folderCount = item.kind === "folder" ? folderCountLine(item) : null
  const folderTypes = item.kind === "folder" ? folderContentTypeLine(item.contentTypes) : null
  const folderTags = item.kind === "folder" ? folderTagLine(item.tags) : null

  const dropdownItems = !isFolder ? (
    <DocumentPeekMenuItems
      title={name}
      copyPath={copyPath}
      addToChat={{
        type: "document",
        id: documentId,
        title: name,
        name,
        documentType: showQuizUi ? "quiz" : (docType ?? "note"),
        path: resolvedFilePath,
      }}
    />
  ) : undefined

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------

  return (
    <div
      ref={paneRef}
      tabIndex={-1}
      className="h-full flex flex-col bg-card overflow-hidden outline-none relative"
      data-testid={`${testIds.documentList.previewMarkdown}-pane`}
    >
      <PreviewPaneHeader
        row
        testId={testIds.documentList.previewHeader}
        icon={headerIcon}
        label={undefined}
        chips={headerChips}
        dropdownContent={dropdownItems}
        dropdownTestId={testIds.documentList.previewActions}
        onOpen={onOpen}
        openTitle={isFolder ? "Open folder" : showQuizUi ? "Open quiz" : "Open in tab"}
        onClose={onClose}
      />

      {/* "Add to chat" subbar — shown for bet documents */}
      {isBet && !isFolder && (
        <div className="px-4 py-2 border-b border-border flex justify-end">
          <button
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-blue-400 transition-colors"
            onClick={() => {
              window.dispatchEvent(
                new CustomEvent("add-to-chat", {
                  detail: {
                    type: "document",
                    id: documentId,
                    title: name,
                    name,
                    documentType: "bet",
                    path: resolvedFilePath,
                  },
                }),
              )
              toast.success("Bet added to chat", {
                description: "Ask me to help shape it into an implementation-ready handoff",
              })
            }}
          >
            <span>🔷</span>
            Shape in chat
          </button>
        </div>
      )}

      {/* Content area */}
      <div
        className="flex-1 min-h-0 overflow-hidden relative"
        data-testid={testIds.documentList.previewMarkdown}
      >
        {findOpen && findEnabled && (
          <PreviewFindBar
            query={findQuery}
            current={findCurrent}
            total={findTotal}
            onQueryChange={setFindQuery}
            onNext={findNext}
            onPrev={findPrev}
            onClose={closeFind}
            focusRequestId={findFocusRequestId}
          />
        )}
        {item.kind === "folder" ? (
          <div className="h-full min-h-0 overflow-y-auto">
            {folderCount || folderTypes || folderTags ? (
              <div
                className="space-y-0.5 border-b border-border px-4 py-2 text-xs text-muted-foreground"
                data-testid={testIds.documentList.previewFolderMeta}
              >
                {folderCount ? <p className="truncate">{folderCount}</p> : null}
                {folderTypes ? <p className="truncate">{folderTypes}</p> : null}
                {folderTags ? <p className="truncate">{folderTags}</p> : null}
              </div>
            ) : null}
            <FolderPeekList folderPath={item.path} onSelectChild={onSelectChild} />
          </div>
        ) : previewLoading ? (
          <div className="flex items-center gap-2 px-4 py-4 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin shrink-0" />
            Loading preview…
          </div>
        ) : previewError ? (
          <div className="px-4 py-4 text-sm text-destructive">{previewError}</div>
        ) : quizDocument ? (
          <div className="h-full min-h-0 overflow-hidden" data-testid={testIds.home.quizPreview}>
            <QuizDocumentRenderer
              document={quizDocument}
              sourceViewActive={false}
              syncTabToUrl={false}
            />
          </div>
        ) : isHtml ? (
          <div className="h-full min-h-0 overflow-hidden">
            <HtmlDocumentViewer html={previewMarkdown} documentTitle={name} />
          </div>
        ) : isCsv ? (
          <div className="h-full min-h-0 overflow-hidden">
            <CsvDocumentViewer content={previewMarkdown} documentTitle={name} readOnly />
          </div>
        ) : imagePreviewDocument ? (
          <div className="h-full min-h-0 overflow-hidden">
            <ImageDocumentViewer document={imagePreviewDocument} sourceViewActive={false} />
          </div>
        ) : (
          <div ref={contentRef} className="h-full min-h-0 overflow-y-auto">
            <MarkdownPreview
              content={previewMarkdown}
              className="max-w-none"
              readingColumnPadding="panel"
            />
          </div>
        )}
      </div>
    </div>
  )
}
